import { useState } from "react";
import { Button, Tooltip } from "@heroui/react";
import { Plus, Trash } from "@phosphor-icons/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import { TemplateType } from "./types";

interface TemplateTypesSectionProps {
  templateTypes: TemplateType[];
  onCreateTypeClick: () => void;
  onTypesChange: () => void;
}

export const TemplateTypesSection: React.FC<TemplateTypesSectionProps> = ({
  templateTypes,
  onCreateTypeClick,
  onTypesChange
}) => {
  const { session } = useSession();
  const [deletingTypeId, setDeletingTypeId] = useState<number | null>(null);

  const handleDeleteType = async (typeId: number, typeName: string) => {
    if (!confirm(`Weet je zeker dat je het type "${typeName}" wilt verwijderen?`)) {
      return;
    }

    setDeletingTypeId(typeId);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/mail-templates/types/${typeId}`,
        { method: "DELETE" },
        session
      );

      if (response.ok) {
        toast.success("Type succesvol verwijderd");
        onTypesChange();
      }
      else {
        throw new Error("Failed to delete type");
      }
    } catch (error) {
      console.error("Error deleting type:", error);
      toast.error("Fout bij verwijderen van type");
    } finally {
      setDeletingTypeId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-neutral-primary">Modules beheren</h3>
        <Button
          startContent={<Plus size={16} />}
          className="bg-moss-primary text-white"
          onPress={onCreateTypeClick}
        >
          Nieuw type
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {templateTypes.length > 0 ? (
          templateTypes.map((type) => (
            <div
              key={type.id}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-neutral-primary/10 rounded-lg"
            >
              <span className="text-sm font-medium">{type.name}</span>
              <div className="flex items-center gap-1 ml-2">
                <Tooltip content="Type verwijderen">
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    className="min-w-6 w-6 h-6 text-danger hover:bg-danger/10"
                    onPress={() => handleDeleteType(type.id!, type.name)}
                    isLoading={deletingTypeId === type.id}
                  >
                    <Trash size={12} />
                  </Button>
                </Tooltip>
              </div>
            </div>
          ))
        ) : (
          <p className="text-sm text-neutral-secondary-mid italic">
            Geen modules beschikbaar. Maak eerst een module aan.
          </p>
        )}
      </div>
    </div>
  );
}; 